import React, { useEffect, useMemo } from 'react';

import { sortBy } from 'lodash';
import { getStringWithoutHash } from 'utilitys/helpers/general';
import { WidgetView } from 'utilitys/reporting/widgetViewModeUtils';

import { useConfigurationStore } from 'store/configurationsStore';
import { useGlobalFilterDataStore } from 'store/globalFilterData.store';

import { useQueryPortfolioTopHeroes } from 'api/queries/getPortfolioTopHeroes.query';
import { IPortfolioTopHeroesItems } from 'api/queries/getProjectScoreCardsSummary';

import { ExportDropdown } from 'components/common/ExportDropdown/exportDropdown';
import { FilterNames, getFiltersData } from 'components/common/GlobalFiltersInPage/globalFiltersInPage.utils';
import NoData from 'components/common/NoData/noData';
import OverlayWithSpinner from 'components/common/OverlayWithSpinner/overlayWithSpinner';
import { TooltipIcon } from 'components/common/TooltipIcon/tooltipIcon';
import { prepareVersionSelectTitle } from 'components/common/TreeProgramFilter/VersionSelect/versionSelect.utils';
import { WidgetTitle } from 'components/common/WidgetTitle/WidgetTitle';
import { CardItem } from 'components/common/WidgetWithCards/WidgetCard/CardItem/CardItem';
import { WidgetWithTitle } from 'components/common/WidgetWithTitle/WidgetWithTitle';

import { AddWidget } from 'components/Dashboards/Project/Components/CustomDashboard/AddRemoveWidget/addWidget';
import { IPortfolioProps } from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import { ThreeDotsMenu } from 'components/Dashboards/Reporting/threeDotsMenu/ThreeDotsMenu';
import { LoaderContainer, WidgetCard } from 'components/common';
import classes from 'components/widgets/OverallStatus/OverallStatus.module.scss';
import { getCardItemValue, serializeoverAllStatusData } from 'components/widgets/OverallStatus/OverallStatus.utils';
import { CardItemProgressBar } from 'components/widgets/OverallStatus/components/CardItemProgressBar/CardItemProgressBar';

import { getColorFromStatus } from 'utilitys/Status/Status.utils';

// const overallStatusId = 'overall-status';

export const OverallStatus = ({
    widgetId,
    externalComponents = null,
    editNarrative = null,
    setSubTitleForExport,
    filter,
    widgetViewMode = WidgetView.page,
}: IPortfolioProps) => {
    const {
        name: configTitle,
        routeId: overallStatusId,
        componentKey,
        endpoints,
    } = useConfigurationStore().overallStatus;
    const globalFilterData = useGlobalFilterDataStore((state) => state.globalFilterData);
    const filters = useMemo(
        () =>
            widgetId
                ? filter?.filters || []
                : getFiltersData({
                      [FilterNames.TAG_PORTFOLIO_BOARD]: globalFilterData?.programTags ?? [],
                  }),

        [filter, globalFilterData],
    );
    const {
        data: overAllStatus,
        isLoading: isLoadingoverAllStatus,
        isFetching: isFetchingoverAllStatus,
    } = useQueryPortfolioTopHeroes({ filter: { filters } }, endpoints.getPortfolioTopHeroes);

    const subTitle = (
        <>
            Total <span className={classes.totalCount}>{overAllStatus?.numOfItems ?? 0}</span> projects/sub-programs
        </>
    );

    const overAllStatusData = serializeoverAllStatusData({ data: overAllStatus?.componentsList });
    const route = `${getStringWithoutHash(window.location.href)}#${overallStatusId}`;

    const subTitleForExport = `${configTitle}, ${prepareVersionSelectTitle()}`;

    useEffect(() => {
        setSubTitleForExport && setSubTitleForExport({ widgetId, subTitle: subTitleForExport, title: configTitle });
    }, [widgetId, subTitleForExport, configTitle]);

    return (
        <WidgetWithTitle
            title={<WidgetTitle title={configTitle} />}
            id={overallStatusId}
            titleComponents={[
                <span
                    style={{ position: 'relative', bottom: '5px' }}
                    key={'subTitle'}
                    className={classes.subProjectTitle}
                >
                    {subTitle}
                </span>,
                externalComponents && <div key={'externalComponents'}>{externalComponents}</div>,
                <AddWidget
                    key={'AddWidget'}
                    componentKey={componentKey}
                    title={configTitle}
                    widgetId={widgetId}
                    route={route}
                    projectId={null}
                    contractId={null}
                    filters={filters}
                    shouldHide={true}
                />,
                <ExportDropdown key={'export'} title={configTitle} subTitle={subTitleForExport} />,
                <TooltipIcon key={'info'} tooltip={overAllStatus?.info} />,
                <ThreeDotsMenu
                    widgetViewMode={widgetViewMode}
                    widgetId={widgetId}
                    componentKey={componentKey}
                    shareTitle={configTitle}
                    shareLink={route}
                />,
            ]}
            titleFilters={[editNarrative && <div key={'editNarrative'}>{editNarrative}</div>]}
        >
            <div className={classes.widgetCardsWrapper}>
                {(isLoadingoverAllStatus || isFetchingoverAllStatus) && (
                    <LoaderContainer height={'500px'}>
                        <OverlayWithSpinner />
                    </LoaderContainer>
                )}
                {!isLoadingoverAllStatus && !overAllStatusData && <NoData />}
                {!isLoadingoverAllStatus &&
                    overAllStatusData &&
                    Object.entries<IPortfolioTopHeroesItems[]>(overAllStatusData).map(([key, value]) => {
                        const titleText = key.slice(2);
                        return (
                            <WidgetCard
                                id={`overallStatusCard_${key}`}
                                contentHeight={'100%'}
                                titleText={titleText}
                                key={key}
                            >
                                {sortBy(value, ['order']).map((item, index) => {
                                    return (
                                        <CardItem
                                            key={`overallStatus-${item.name}`}
                                            title={item.name}
                                            showTooltip={false}
                                            showCardInfoTooltip={false}
                                            id={`overallStatusCardItem_title_${key}_${index}`}
                                        >
                                            <CardItemProgressBar
                                                id={`overallStatusCardItem_value_${key}_${index}`}
                                                mainText={item.value}
                                                currentValue={getCardItemValue(item.value)}
                                                textColor={getColorFromStatus(item.name, titleText)}
                                                maxValue={overAllStatus?.numOfItems}
                                            />
                                        </CardItem>
                                    );
                                })}
                            </WidgetCard>
                        );
                    })}
            </div>
        </WidgetWithTitle>
    );
};
